import { useRef, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShareImageButton } from "@/components/ShareCard";
import { useLang } from "@/context/LanguageContext";

const S = 1080;
const day = (iso) => new Date(iso || Date.now()).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
const fileName = (lesson, name, ext) => `RoboSpark-Class-Star-Lesson-${lesson.n}-${(name || "Student").replace(/\s+/g, "-")}.${ext}`;

const starPath = (ctx, cx, cy, r) => {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? r * 0.45 : r;
    ctx.lineTo(cx + rr * Math.cos(a), cy + rr * Math.sin(a));
  }
  ctx.closePath();
};

// Word-wrap to at most `max` lines, ending with an ellipsis when the text is longer.
export const wrap = (ctx, text, width, max) => {
  const lines = [];
  let cur = "";
  for (const w of text.split(" ")) {
    const next = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(next).width <= width) { cur = next; continue; }
    lines.push(cur); cur = w;
    if (lines.length === max) break;
  }
  if (lines.length < max) { lines.push(cur); return lines; }
  let last = lines[max - 1];
  while (ctx.measureText(`${last}…`).width > width) last = last.slice(0, -1);
  lines[max - 1] = `${last.trimEnd()}…`;
  return lines;
};

export const loadImg = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });

// 1080x1080 share image for WhatsApp / Instagram.
const drawStarCard = async ({ lesson, name, badgeNo }) => {
  await document.fonts?.ready;
  const c = Object.assign(document.createElement("canvas"), { width: S, height: S });
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#E8F0FE"; ctx.fillRect(0, 0, S, S);
  try {
    const img = await loadImg(lesson.teacher);
    const r = Math.max(S / img.width, 600 / img.height);
    ctx.drawImage(img, (img.width - S / r) / 2, (img.height - 600 / r) / 2, S / r, 600 / r, 0, 0, S, 600);
  } catch { /* plain background */ }
  const g = ctx.createLinearGradient(0, 330, 0, 640);
  g.addColorStop(0, "rgba(232,240,254,0)"); g.addColorStop(1, "#E8F0FE");
  ctx.fillStyle = g; ctx.fillRect(0, 330, S, 310);
  ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.roundRect(50, 50, 300, 84, 22); ctx.fill();
  ctx.fillStyle = "#FF2E63"; ctx.beginPath(); ctx.roundRect(66, 66, 52, 52, 12); ctx.fill();
  ctx.font = "800 38px Outfit, sans-serif"; ctx.textAlign = "left";
  ctx.fillStyle = "#FF2E63"; ctx.fillText("Robo", 134, 106);
  ctx.fillStyle = "#0A192F"; ctx.fillText("Spark", 134 + ctx.measureText("Robo").width, 106);
  ctx.save(); ctx.translate(S - 60, 60); ctx.rotate(0.05);
  ctx.fillStyle = "#FFC94D"; ctx.beginPath(); ctx.roundRect(-360, 0, 360, 120, 24); ctx.fill();
  ctx.fillStyle = "#0A192F"; ctx.textAlign = "right"; ctx.font = "800 24px Outfit, 'Noto Sans Devanagari', sans-serif"; ctx.fillText("CLASS STAR · क्लास स्टार", -30, 44);
  for (let i = 0; i < 5; i++) { ctx.fillStyle = "#B45309"; starPath(ctx, -300 + i * 56, 82, 20); ctx.fill(); }
  ctx.restore();
  if (badgeNo) {
    const qr = await loadImg(await QRCode.toDataURL(`${window.location.origin}/verify-star/${badgeNo}`, { margin: 1, width: 300, color: { dark: "#0A192F" } }));
    ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.roundRect(S - 240, 210, 180, 214, 20); ctx.fill();
    ctx.drawImage(qr, S - 225, 225, 150, 150);
    ctx.fillStyle = "#0A192F"; ctx.textAlign = "center"; ctx.font = "700 17px Outfit, sans-serif"; ctx.fillText(badgeNo, S - 150, 405);
  }
  ctx.textAlign = "left"; ctx.fillStyle = "#007A8A"; ctx.font = "800 30px Outfit, sans-serif";
  ctx.fillText("5/5 STARS · VIRTUAL CLASS WITH ZOYA MA'AM", 70, 700);
  ctx.fillStyle = "#0A192F"; ctx.font = "900 118px Outfit, sans-serif"; ctx.fillText((name || "Student").split(" ")[0].slice(0, 14), 64, 830);
  ctx.fillStyle = "#334155"; ctx.font = "600 34px Outfit, 'Noto Sans Devanagari', sans-serif";
  wrap(ctx, `Lesson ${lesson.n} · ${lesson.title}`, S - 140, 2).forEach((line, i) => ctx.fillText(line, 70, 892 + i * 44));
  ctx.fillStyle = "#FF2E63"; ctx.fillRect(70, 990, 130, 9);
  ctx.fillStyle = "#64748B"; ctx.font = "500 24px Outfit, sans-serif"; ctx.textAlign = "right";
  ctx.fillText("Learn · Build · Earn  —  robospark", S - 70, 1002);
  return new Promise((res) => c.toBlob(res, "image/png"));
};

const Star = ({ size = 34 }) => <span style={{ fontSize: size, color: "#F5A524", lineHeight: 1 }}>★</span>;

// A4-landscape PDF certificate.
const StarDoc = ({ lesson, name, badgeNo, at, qr, innerRef }) => (
  <div ref={innerRef} style={{ width: 1123, height: 794, background: "#E8F0FE", fontFamily: "'Outfit','Noto Sans Devanagari',sans-serif", padding: 28, boxSizing: "border-box" }}>
    <div style={{ background: "#fff", border: "3px solid #00B8D4", borderRadius: 24, height: "100%", boxSizing: "border-box", padding: "40px 70px", textAlign: "center", color: "#0A192F", position: "relative",
      backgroundImage: "radial-gradient(600px 300px at 50% 0%, rgba(255,201,77,0.28), transparent), radial-gradient(500px 280px at 100% 100%, rgba(0,229,255,0.16), transparent)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 22, fontWeight: 800 }}>
        <span><span style={{ color: "#FF2E63" }}>Robo</span>Spark <span style={{ fontSize: 11, letterSpacing: 3, color: "#64748B" }}>PRP PVT LTD</span></span>
        <span style={{ fontSize: 13, letterSpacing: 3, color: "#007A8A" }}>VIRTUAL CLASSROOM</span>
      </div>
      <div style={{ marginTop: 26, fontSize: 15, color: "#007A8A", fontWeight: 700 }}>
        <span style={{ letterSpacing: 6 }}>CERTIFICATE OF ACHIEVEMENT · </span>
        <span style={{ letterSpacing: 0, fontFamily: "'Noto Sans Devanagari',sans-serif", fontSize: 17 }}>उपलब्धि प्रमाणपत्र</span>
      </div>
      <div style={{ marginTop: 8, fontSize: 64, fontWeight: 900, color: "#B45309", letterSpacing: 3 }}>CLASS STAR</div>
      <div style={{ marginTop: 2 }}>{[0, 1, 2, 3, 4].map((i) => <Star key={i} />)}</div>
      <div style={{ marginTop: 22, fontSize: 15, color: "#64748B" }}>Awarded to · प्रदान किया गया</div>
      <div style={{ marginTop: 6, fontSize: 48, fontWeight: 800, borderBottom: "3px solid #FF2E63", display: "inline-block", padding: "0 30px 6px" }}>{name || "Student"}</div>
      <div style={{ marginTop: 18, fontSize: 17, color: "#334155", lineHeight: 1.7 }}>
        for earning all <b style={{ color: "#B45309" }}>5 stars</b> in <b>Lesson {lesson.n}: {lesson.title}</b> with Zoya Ma'am<br />
        <span style={{ fontSize: 15 }}>present in class · 3/3 quiz stars · full lesson watched — {lesson.title_hi}</span>
      </div>
      <div style={{ position: "absolute", left: 70, right: 70, bottom: 38, display: "flex", justifyContent: "space-between", alignItems: "flex-end", fontSize: 13 }}>
        <div style={{ textAlign: "left", color: "#334155", display: "flex", alignItems: "flex-end", gap: 14 }}>
          {qr && <img src={qr} alt="" style={{ width: 96, height: 96, border: "1px solid #CBD5E1", borderRadius: 8 }} />}
          <div>Badge No: <b>{badgeNo}</b><br />Date: <b>{day(at)}</b><br /><span style={{ fontSize: 11, color: "#007A8A" }}>Scan to verify · /verify-star</span></div>
        </div>
        <div style={{ textAlign: "center" }}>
          <img src={lesson.teacher} alt="" style={{ width: 92, height: 92, borderRadius: "50%", objectFit: "cover", border: "4px solid #FFC94D" }} />
          <div style={{ fontSize: 12, color: "#007A8A", fontWeight: 700 }}>Zoya Ma'am · AI Teacher</div>
        </div>
        <div style={{ textAlign: "right" }}><div style={{ borderTop: "1px solid #0A192F", paddingTop: 4, width: 220 }}>Director, RoboSpark PrP Pvt Ltd</div><span style={{ fontSize: 11, color: "#64748B" }}>C-30, C-Block, Sector 63, Noida-201301 (U.P.)</span></div>
      </div>
    </div>
  </div>
);

export const ClassStarActions = ({ lesson, name, badgeNo, at, testid = "classstar" }) => {
  const { L } = useLang();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState("");
  const pdf = async () => {
    setBusy(true);
    try {
      setQr(await QRCode.toDataURL(`${window.location.origin}/verify-star/${badgeNo}`, { margin: 1, width: 240, color: { dark: "#0A192F" } }));
      const html2pdf = (await import("html2pdf.js")).default;
      await new Promise((r) => setTimeout(r, 30)); // certificate is mounted only while generating
      await Promise.all([...ref.current.querySelectorAll("img")].map((i) => i.decode().catch(() => {})));
      await html2pdf().set({
        margin: 0, filename: fileName(lesson, name, "pdf"), image: { type: "jpeg", quality: 0.95 }, html2canvas: { scale: 2 },
        jsPDF: { unit: "px", format: [1123, 794], orientation: "landscape", hotfixes: ["px_scaling"] },
      }).from(ref.current).save();
    } catch { toast.error(L("Could not create the certificate.", "प्रमाणपत्र नहीं बन सका।")); } finally { setBusy(false); }
  };
  const first = (name || "").split(" ")[0];
  const text = L(`${first} is a RoboSpark Class Star: 5/5 stars in Lesson ${lesson.n} (${lesson.title}) with Zoya Ma'am! ${window.location.origin}/virtual-classroom`,
    `${first} RoboSpark क्लास स्टार हैं: ज़ोया मैम के पाठ ${lesson.n} (${lesson.title_hi}) में 5/5 स्टार! ${window.location.origin}/virtual-classroom`);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      <Button onClick={pdf} disabled={busy} className="rounded-full bg-[#FF3B3B] hover:bg-[#E63946] text-white" data-testid={`${testid}-pdf-btn`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />{L("Certificate PDF", "प्रमाणपत्र PDF")}</>}
      </Button>
      <ShareImageButton makeBlob={() => drawStarCard({ lesson, name, badgeNo })} fname={fileName(lesson, name, "png")} text={text}
        title={L("Share your Class Star", "अपना क्लास स्टार शेयर करें")} testid={`${testid}-share-btn`} />
      {busy && <div aria-hidden style={{ position: "fixed", left: -14000, top: 0 }}><StarDoc lesson={lesson} name={name} badgeNo={badgeNo} at={at} qr={qr} innerRef={ref} /></div>}
    </div>
  );
};
